/**
 * Finding something when you can't remember which appliance it's in.
 *
 * The kitchen is deliberately three closed doors, which is the right default —
 * it makes the screen a place rather than a list. It is also exactly wrong when
 * you already know what you're looking for and just want to know whether you
 * have it, which is the question people actually open a food app to answer.
 *
 * Matching is the same shape the recipe matcher uses — every word you typed has
 * to appear somewhere in the name — so "chick th" finds "Chicken thighs" and
 * "th chick" finds it too. Substring rather than whole-word, because a search
 * box is a different promise from an ingredient match: half a word typed is a
 * search in progress, not a mistake.
 */

import { daysUntilExpiry } from "./expiry";
import type { FoodItem } from "./types";

/** Enough to be a search rather than a browse. */
const MIN_QUERY = 1;

export function normalise(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9\s]/g, " ").replace(/\s+/g, " ").trim();
}

export function matchesQuery(item: FoodItem, query: string): boolean {
  const words = normalise(query).split(" ").filter(Boolean);
  if (words.length === 0) return false;

  const haystack = normalise(item.name);
  return words.every((word) => haystack.includes(word));
}

/**
 * Everything matching, soonest to spoil first.
 *
 * Sorted by expiry rather than by name or by place: a search across the whole
 * kitchen is a list you scan, and the thing you most want to see first is the
 * one you have least time to use. Place is shown on the row instead, which is
 * the fact the three-door layout was carrying and the one this would otherwise
 * lose.
 */
export function searchItems(items: FoodItem[], query: string, today: string): FoodItem[] {
  if (normalise(query).length < MIN_QUERY) return [];

  return items
    .filter((item) => matchesQuery(item, query))
    .sort((a, b) => {
      const byExpiry = daysUntilExpiry(a, today) - daysUntilExpiry(b, today);
      return byExpiry !== 0 ? byExpiry : a.name.localeCompare(b.name);
    });
}
